import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class SignalRService {
	private hubConnection: signalR.HubConnection;

	/**
	 * Establishes a WebSocket connection to the SignalR hub
	 * @returns Promise<void> - Resolves when connection is established, rejects on connection failure
	 */
	public startConnection = (): Promise<void> => {
		this.hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(`${environment.apiUrl}/feature-flag-hub`, {
				skipNegotiation: false,
				transport: signalR.HttpTransportType.WebSockets,
			})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Information)
			.build();

		return this.hubConnection
			.start()
			.then(() => {
				console.log('SignalR connection established');
			})
			.catch((err) => {
				console.log(`Error while starting connection: ${err}`);
				throw err;
			});
	};

	/**
	 * Ends a WebSocket connection to the SignalR hub
	 */
	public stopConnection(): void {
		if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
			this.hubConnection.stop()
				.then(() => console.log('SignalR Connection stopped.'))
				.catch(err => console.error(`Error while stopping connection: ${err}`));
		}
	}

	/**
	 * Sets up a listener for incoming messages from the SignalR hub
	 * Listens for "ReceiveMessage" events and executes the provided callback
	 * @param callback - Function to execute when a message is received. Receives featureFlagAccess object
	 */
	public addFeatureFlagDataListener = (callback) => {
		this.hubConnection.on('ReceiveMessage', (featureFlagAccess) => {
			callback(featureFlagAccess);
		});
	};

	/**
	 * Sends a message to all connected clients via the SignalR hub
	 * @param user - The name of the user sending the message
	 * @param message - The message content to send
	 */
	public receiveMessage(teanantId: string, featureFlagKey: string) {
		if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
			console.error('SignalR connection is not in Connected state');
			return;
		}

		this.hubConnection
			.invoke('SendMessage', teanantId, featureFlagKey)
			.catch((err) => {
				console.error('Error sending message:', err);
			});
	}

	/**
	 * Triggers a message broadcast from the server-side API endpoint
	 * Sends a predefined system message via HTTP POST to the broadcast endpoint
	 */
	public broadcastMessageFromServer() {
		const message =
			'This message is an example of a message broadcasted from the API';
		const user = 'SystemAdmin';
		const url = `${environment.apiUrl}/feature-flag-hub/broadcast?user=${user}&message=${message}`;

		fetch(url, {
			method: 'POST',
		}).then((response) => {
			if (!response.ok) {
				console.error('Failed to broadcast message', response);
			}
		});
	}
}
